import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DollarSign, Package, TrendingUp, Shield, Calendar,
  FileText, ChevronDown, Download, RefreshCw,
  Sparkles, AlertTriangle, CheckCircle, Eye,
  Zap, Search, Loader2, FileSpreadsheet,
  ArrowUpRight, ExternalLink, Cpu, Repeat, Activity,
  BarChart3,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { format as fmtDate, startOfYear, subDays, subMonths } from 'date-fns';
import api from '@/services/api';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '@/context/CurrencyContext';
import { useAuth } from '@/hooks/useAuth';
import InfoTip from '@/components/InfoTip';
import SiteDeptFilter from '@/components/SiteDeptFilter';

const getPresetDates = (preset) => {
  const today = new Date();
  switch (preset) {
    case '30d': return { from: subDays(today, 30), to: today };
    case '90d': return { from: subDays(today, 90), to: today };
    case 'ytd': return { from: startOfYear(today), to: today };
    case 'fy': {
      const fyStart = today.getMonth() >= 3 ? new Date(today.getFullYear(), 3, 1) : new Date(today.getFullYear() - 1, 3, 1);
      return { from: fyStart, to: today };
    }
    case '12m': return { from: subMonths(today, 12), to: today };
    default: return { from: null, to: null };
  }
};

const RISK_COLORS = {
  'Lifecycle Risk': { bg: 'bg-red-100', text: 'text-red-800' },
  'Price Gap': { bg: 'bg-amber-100', text: 'text-amber-800' },
  'Price Jump': { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  'Single Source': { bg: 'bg-orange-100', text: 'text-orange-800' },
  'Supply Risk': { bg: 'bg-orange-100', text: 'text-orange-800' },
};
const getCategoryColor = (cat) => RISK_COLORS[cat] || { bg: 'bg-gray-100', text: 'text-gray-800' };

const Dashboard = () => {
  const navigate = useNavigate();
  const { format: formatCurrency } = useCurrency();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [datePreset, setDatePreset] = useState('12m');
  const [dateRange, setDateRange] = useState(() => getPresetDates('12m'));
  const [bomFilter, setBomFilter] = useState('all');
  const [bomSearch, setBomSearch] = useState('');
  const [filterSite, setFilterSite] = useState('All Sites');
  const [filterDept, setFilterDept] = useState('All Departments');
  const [priorityLimit, setPriorityLimit] = useState('5');
  const [actionModal, setActionModal] = useState(null);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/dashboard/metrics');
      setMetrics(res.data);
    } catch { setMetrics(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const handleDatePreset = (preset) => {
    setDatePreset(preset);
    if (preset !== 'custom') setDateRange(getPresetDates(preset));
  };

  const handleExport = async (fmt) => {
    setExporting(true);
    try {
      const res = await api.get(`/dashboard/export?fmt=${fmt}`, { responseType: 'blob' });
      const ext = fmt === 'csv' ? 'csv' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `dashboard-export.${ext}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { /* */ }
    finally { setExporting(false); }
  };

  if (loading && !metrics) {
    return (
      <Layout title="Dashboard" subtitle="Your procurement intelligence summary">
        <div className="flex items-center justify-center h-64"><RefreshCw className="h-8 w-8 animate-spin text-green-600" /></div>
      </Layout>
    );
  }

  const tiles = metrics?.tiles || {};
  const km = metrics?.key_metrics || {};
  const bomList = metrics?.bom_list || [];
  const monthlySavings = metrics?.monthly_savings || [];
  const monthlyRisks = metrics?.monthly_risks || [];
  const hasBoms = km.total_boms > 0;

  const filteredBoms = bomList.filter(b => !bomSearch || b.name?.toLowerCase().includes(bomSearch.toLowerCase()));

  // Priority actions (P0/P1 for logged-in user)
  const priorityActions = [
    { id: 1, priority: 'P0', mpn: 'LAN8720A-CP-TR', bom: 'Main Controller Board v2.1', desc: 'Going NRND Q3 2026 — 3 FFF alternates available, lowest at -40% cost', impact: 'High', category: 'Lifecycle Risk' },
    { id: 2, priority: 'P0', mpn: 'NRF52840-QIAA-R', bom: 'IoT Sensor Module v1.3', desc: 'Paying 28% above weighted median — renegotiate or switch to authorized distributor', impact: 'High', category: 'Price Gap', value: '$18,200' },
    { id: 3, priority: 'P1', mpn: 'IS42S32800J-7TLI', bom: 'Main Controller Board v2.1', desc: 'AI predicts +22% price increase in 60 days — consider early procurement', impact: 'High', category: 'Price Jump', value: '$12,800' },
    { id: 4, priority: 'P1', mpn: 'STM32H755ZIT6', bom: 'Power Management Unit v3.0', desc: 'Single-source risk — no qualified second source, 500+ units/month', impact: 'Medium', category: 'Single Source' },
    { id: 5, priority: 'P1', mpn: 'ESP32-WROOM-32E', bom: 'IoT Sensor Module v1.3', desc: 'Distributor efficiency at 72% — switch to DigiKey saves $0.40/unit', impact: 'Medium', category: 'Price Gap', value: '$8,000' },
  ];
  const visibleActions = priorityLimit === 'all' ? priorityActions : priorityActions.slice(0, parseInt(priorityLimit) || 5);

  // Build tile groups — always show all
  const tileGroups = [];
  const bom = tiles.bom || {};
  tileGroups.push({
    title: 'BOM Intelligence',
    icon: FileText,
    color: 'blue',
    items: [
      { label: 'BOMs Optimized', value: bom.optimizations || 0 },
      { label: 'Savings Identified', value: formatCurrency(bom.savings_identified || 0), raw: bom.savings_identified },
      { label: 'Savings Realized', value: formatCurrency(bom.savings_achieved || 0), raw: bom.savings_achieved },
    ],
  });
  const bids = tiles.bids || {};
  tileGroups.push({
    title: 'Bid Intelligence',
    icon: DollarSign,
    color: 'purple',
    items: [
      { label: 'Bids Optimized', value: bids.optimizations || 0 },
      { label: 'Savings Identified', value: formatCurrency(bids.savings_identified || 0), raw: bids.savings_identified },
      { label: 'Savings Realized', value: formatCurrency(bids.savings_achieved || 0), raw: bids.savings_achieved },
    ],
  });
  const design = tiles.design || {};
  tileGroups.push({
    title: 'Design Intelligence',
    icon: Cpu,
    color: 'cyan',
    items: [
      { label: 'Designs Analyzed', value: design.designs_analyzed || 0 },
      { label: 'Components Reviewed', value: design.components_analyzed || 0 },
    ],
  });
  const alts = tiles.alternates || {};
  tileGroups.push({
    title: 'Alternate Intelligence',
    icon: Repeat,
    color: 'green',
    items: [
      { label: 'Alternates Identified', value: alts.identified || 0, sub: 'via BOM & Design Analysis' },
      { label: 'Alternates Accepted', value: alts.accepted || 0 },
    ],
  });
  const risks = tiles.risks || {};
  tileGroups.push({
    title: 'Risk Intelligence',
    icon: Shield,
    color: 'red',
    items: [
      { label: 'Lifecycle Risks', value: risks.lifecycle_risks || 0, sub: 'via BOM & Design Analysis' },
      { label: 'Supply Chain Risks', value: risks.supply_chain_risks || 0, sub: 'via BOM & Design Analysis' },
      { label: 'Risks Actioned', value: risks.risks_actioned || 0 },
    ],
  });

  const colorMap = {
    blue: { card: 'from-blue-50 to-cyan-50 border-blue-200 hover:border-blue-300', val: 'text-blue-700', lbl: 'text-blue-600', icon: 'text-blue-400' },
    purple: { card: 'from-purple-50 to-violet-50 border-purple-200 hover:border-purple-300', val: 'text-purple-700', lbl: 'text-purple-600', icon: 'text-purple-400' },
    cyan: { card: 'from-cyan-50 to-teal-50 border-cyan-200 hover:border-cyan-300', val: 'text-cyan-700', lbl: 'text-cyan-600', icon: 'text-cyan-400' },
    green: { card: 'from-green-50 to-emerald-50 border-green-200 hover:border-green-300', val: 'text-green-700', lbl: 'text-green-600', icon: 'text-green-400' },
    red: { card: 'from-red-50 to-orange-50 border-red-200 hover:border-red-300', val: 'text-red-700', lbl: 'text-red-600', icon: 'text-red-400' },
  };

  return (
    <Layout title="Dashboard" subtitle="Your procurement intelligence summary" breadcrumbs={[{ label: 'Dashboard' }]}>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Welcome */}
        <div className="flex items-center justify-between" data-testid="welcome-section">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Welcome back, {user?.name || 'User'}</h3>
            <p className="text-xs text-gray-400">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>

        {!hasBoms ? (
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-sm" data-testid="empty-state">
            <CardContent className="py-12 text-center space-y-4">
              <Package className="h-16 w-16 text-green-400 mx-auto" />
              <h3 className="text-xl font-bold text-gray-900">Get Started with 1Data</h3>
              <p className="text-sm text-gray-600 max-w-md mx-auto">Upload your first Bill of Materials to unlock savings intelligence, risk analysis, and procurement optimization.</p>
              <Button onClick={() => navigate('/import')} className="bg-green-600 hover:bg-green-700 text-white h-11 px-6" data-testid="get-started-btn">
                <ArrowUpRight className="h-4 w-4 mr-2" /> Upload Your First BOM
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Intelligence Tiles — auto-hide groups with no data */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="intelligence-tiles">
              {tileGroups.map(group => {
                const c = colorMap[group.color];
                const Icon = group.icon;
                return (
                  <Card key={group.title} className={`bg-gradient-to-br ${c.card} shadow-sm hover:shadow-sm transition-all`} data-testid={`tile-${group.title.toLowerCase()}`}>
                    <CardContent className="pt-4 pb-3 px-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Icon className={`h-4 w-4 ${c.icon}`} />
                        <p className={`text-xs font-bold uppercase tracking-wider ${c.lbl}`}>{group.title}</p>
                      </div>
                      <div className="space-y-2">
                        {group.items.map((item, i) => (
                          <div key={i}>
                            <p className={`text-lg font-bold ${c.val} leading-tight`}>
                              {typeof item.value === 'number' ? item.value.toLocaleString() : item.value}
                            </p>
                            <p className="text-[11px] text-gray-600">{item.label}</p>
                            {item.sub && <p className="text-[9px] text-gray-400">{item.sub}</p>}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Priority Actions (P0/P1 for logged-in user) */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="priority-actions-section">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-gray-900 flex items-center gap-2 text-base">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    Priority Actions
                    <InfoTip text="P0 and P1 actions assigned to you. Take action directly from here." />
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Select value={priorityLimit} onValueChange={setPriorityLimit}>
                      <SelectTrigger className="w-24 h-8" data-testid="priority-limit"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">Top 5</SelectItem>
                        <SelectItem value="10">Top 10</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="priority-actions-list">
                  {visibleActions.map((action, idx) => {
                    const catStyle = getCategoryColor(action.category);
                    return (
                      <div key={action.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-green-300 transition-colors" data-testid={`priority-action-${idx}`}>
                        <div className={`w-1.5 h-12 rounded-full flex-shrink-0 mt-0.5 ${action.priority === 'P0' ? 'bg-red-500' : 'bg-amber-500'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <Badge className={action.priority === 'P0' ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'} data-testid={`action-priority-${idx}`}>{action.priority}</Badge>
                            <span className="font-mono font-semibold text-sm text-gray-900">{action.mpn}</span>
                            <Badge className={`${catStyle.bg} ${catStyle.text} text-[10px]`}>{action.category}</Badge>
                          </div>
                          <p className="text-xs text-blue-600 mb-0.5">{action.bom}</p>
                          <p className="text-sm text-gray-600 leading-snug">{action.desc}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {action.value && <span className="text-sm font-bold text-green-600">{action.value}</span>}
                          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setActionModal(action)} data-testid={`take-action-${idx}`}>
                            <Zap className="h-3 w-3 mr-1" /> Take Action
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Filters with Site & Department */}
            <Card className="bg-white border-gray-200 shadow-sm" data-testid="filters-bar">
              <CardContent className="py-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <Select value={datePreset} onValueChange={handleDatePreset}>
                    <SelectTrigger className="w-40 h-9" data-testid="date-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30d">Last 30 Days</SelectItem>
                      <SelectItem value="90d">Last 90 Days</SelectItem>
                      <SelectItem value="ytd">Year to Date</SelectItem>
                      <SelectItem value="fy">This FY</SelectItem>
                      <SelectItem value="12m">Last 12 Months</SelectItem>
                      <SelectItem value="custom">Custom Range</SelectItem>
                    </SelectContent>
                  </Select>

                  {datePreset === 'custom' && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-9 text-xs gap-1" data-testid="custom-date-btn">
                          <Calendar className="h-3.5 w-3.5" />
                          {dateRange.from ? fmtDate(dateRange.from, 'MMM dd') : 'Start'} — {dateRange.to ? fmtDate(dateRange.to, 'MMM dd, yyyy') : 'End'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent mode="range" selected={{ from: dateRange.from, to: dateRange.to }} onSelect={(range) => setDateRange({ from: range?.from || null, to: range?.to || null })} disabled={{ after: new Date() }} numberOfMonths={2} />
                      </PopoverContent>
                    </Popover>
                  )}

                  <SiteDeptFilter site={filterSite} onSiteChange={setFilterSite} dept={filterDept} onDeptChange={setFilterDept} />

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-9 text-xs gap-1.5 min-w-[160px] justify-start" data-testid="bom-filter">
                        <FileText className="h-3.5 w-3.5 text-gray-400" />
                        {bomFilter === 'all' ? `All BOMs (${km.total_boms})` : bomList.find(b => b.bom_id === bomFilter)?.name || 'BOM'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2" align="start">
                      <div className="relative mb-2">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                        <Input placeholder="Search BOMs..." className="pl-8 h-8 text-xs" value={bomSearch} onChange={e => setBomSearch(e.target.value)} data-testid="bom-search-input" />
                      </div>
                      <div className="max-h-48 overflow-y-auto space-y-0.5">
                        <button className={`w-full text-left text-xs px-2 py-1.5 rounded ${bomFilter === 'all' ? 'bg-green-50 text-green-700 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setBomFilter('all')}>All BOMs ({km.total_boms})</button>
                        {filteredBoms.map(b => (
                          <button key={b.bom_id} className={`w-full text-left text-xs px-2 py-1.5 rounded flex items-center justify-between ${bomFilter === b.bom_id ? 'bg-green-50 text-green-700 font-medium' : 'hover:bg-gray-50'}`} onClick={() => setBomFilter(b.bom_id)}>
                            <span className="truncate">{b.name}</span>
                            <Badge className={`text-[9px] ${b.status === 'optimized' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{b.status}</Badge>
                          </button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>

                  <div className="flex-1" />

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-9 gap-1.5" disabled={exporting} data-testid="export-btn">
                        {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                        Export <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleExport('excel')} data-testid="export-excel">
                        <FileSpreadsheet className="h-3.5 w-3.5 mr-2 text-green-600" /> Excel (.xlsx)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv">
                        <FileText className="h-3.5 w-3.5 mr-2 text-blue-600" /> CSV
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>

            {/* Two Graphs Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Savings Intelligence */}
              <Card className="bg-white border-gray-200 shadow-sm" data-testid="savings-intelligence-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-gray-900 flex items-center gap-2 text-sm">
                    <TrendingUp className="h-4 w-4 text-green-600" />
                    Savings Intelligence
                    <InfoTip text="Month-on-month view. Proposed = price diff + alternates savings identified. Achieved = savings realized through alternate switch, supplier switch, and renegotiation." />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={monthlySavings} barGap={1} barCategoryGap="20%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v, name) => [`$${v.toLocaleString()}`, name]} contentStyle={{ fontSize: 11 }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Bar dataKey="price_diff_proposed" name="Proposed (Price Diff)" stackId="proposed" fill="#86efac" maxBarSize={24} />
                      <Bar dataKey="alternate_proposed" name="Proposed (Alternates)" stackId="proposed" fill="#93c5fd" radius={[3, 3, 0, 0]} maxBarSize={24} />
                      <Bar dataKey="alternate_switch" name="Achieved (Alt. Switch)" stackId="achieved" fill="#16a34a" maxBarSize={24} />
                      <Bar dataKey="supplier_switch" name="Achieved (Supplier Switch)" stackId="achieved" fill="#0284c7" maxBarSize={24} />
                      <Bar dataKey="renegotiation" name="Achieved (Renegotiation)" stackId="achieved" fill="#7c3aed" radius={[3, 3, 0, 0]} maxBarSize={24} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Risk Intelligence */}
              <Card className="bg-white border-gray-200 shadow-sm" data-testid="risk-intelligence-chart">
                <CardHeader className="pb-2">
                  <CardTitle className="text-gray-900 flex items-center gap-2 text-sm">
                    <Shield className="h-4 w-4 text-red-600" />
                    Risk Intelligence
                    <InfoTip text="Month-on-month view of Medium & High risks identified across components for Lifecycle (NRND/EOL/Obsolete), Supply Chain (single-source, lead-time), and Environmental compliance." />
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={monthlyRisks} barGap={1} barCategoryGap="20%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} label={{ value: '# Components', angle: -90, position: 'insideLeft', offset: 10, fontSize: 10 }} />
                      <Tooltip contentStyle={{ fontSize: 11 }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Bar dataKey="lifecycle_high" name="Lifecycle (High)" stackId="lifecycle" fill="#dc2626" maxBarSize={20} />
                      <Bar dataKey="lifecycle_medium" name="Lifecycle (Medium)" stackId="lifecycle" fill="#fca5a5" radius={[3, 3, 0, 0]} maxBarSize={20} />
                      <Bar dataKey="supply_chain_high" name="Supply Chain (High)" stackId="supply" fill="#ea580c" maxBarSize={20} />
                      <Bar dataKey="supply_chain_medium" name="Supply Chain (Medium)" stackId="supply" fill="#fdba74" radius={[3, 3, 0, 0]} maxBarSize={20} />
                      <Bar dataKey="environmental_high" name="Environmental (High)" stackId="env" fill="#7c3aed" maxBarSize={20} />
                      <Bar dataKey="environmental_medium" name="Environmental (Medium)" stackId="env" fill="#c4b5fd" radius={[3, 3, 0, 0]} maxBarSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>

      {/* Action Detail Modal */}
      <Dialog open={!!actionModal} onOpenChange={() => setActionModal(null)}>
        <DialogContent className="max-w-lg" data-testid="action-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500" /> Action Details
            </DialogTitle>
          </DialogHeader>
          {actionModal && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={actionModal.priority === 'P0' ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}>{actionModal.priority}</Badge>
                <Badge className="font-mono">{actionModal.mpn}</Badge>
                <Badge className={`${getCategoryColor(actionModal.category).bg} ${getCategoryColor(actionModal.category).text}`}>{actionModal.category}</Badge>
              </div>
              <p className="text-xs text-blue-600">{actionModal.bom}</p>
              <p className="text-sm text-gray-700">{actionModal.desc}</p>
              {actionModal.value && <p className="text-lg font-bold text-green-600">{actionModal.value} potential savings</p>}
              <div className="flex gap-2 pt-2">
                <Button className="bg-green-600 hover:bg-green-700 text-white flex-1" onClick={() => { setActionModal(null); navigate(`/component-intelligence?mpn=${encodeURIComponent(actionModal.mpn)}`); }} data-testid="action-modal-view-component">
                  <Eye className="h-4 w-4 mr-1" /> View Component
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => { setActionModal(null); navigate('/actions'); }} data-testid="action-modal-go-actions">
                  <ExternalLink className="h-4 w-4 mr-1" /> Action Center
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Dashboard;
